// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE GABARIT, SA FUSION ET SON RENDU — un objet, deux sorties (HTML + texte).
//
// 🔴 POURQUOI CE MODULE EXISTE. L'assistant marketing propose quatre champs de fusion à insérer et son
// commentaire affirme que « le serveur les reconnaît à leur nom français au moment de l'envoi ».
// MESURÉ : personne ne les remplace — `campaign.body` part tel quel au transport. Une campagne écrite
// « Bonjour {prénom}, » envoie littéralement `Bonjour {prénom},` à tous les destinataires. Ce n'est pas
// une fonctionnalité qui manque, c'est un **bouton qui fait semblant** (§23.0), et le commentaire qui
// jure le contraire est ce qui décourage d'aller vérifier.
//
// ⚠️ 🔴 UNE VALEUR MANQUANTE NE LAISSE JAMAIS LE JETON. Chaque champ déclaré porte une valeur de repli
// obligatoire : le rendu ne peut pas produire d'accolade orpheline, quelle que soit la donnée.
//
// ⚠️ 🔴 ET LA VALEUR EST ÉCHAPPÉE AVANT D'ENTRER DANS LE HTML. Un client nommé « Jean <b>Dupont »
// injecterait sinon du balisage dans le corps — et un nom de client vient d'une saisie libre en caisse.
// Le gabarit est du HTML relu par nous ; la VALEUR ne l'est pas.
//
// ⚠️ LA PARTIE TEXTE SE DÉRIVE, ELLE NE SE SAISIT PAS. Un e-mail sans partie texte est pénalisé par
// les filtres et illisible pour qui lit en texte seul ; la saisir deux fois la ferait diverger dès la
// première correction.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { assertFields, TemplateRejected, TOKEN_PATTERN, tokensOf, type MergeField } from './fields';
import { escapeHtml, sanitizeHtml, urlSure } from './sanitize';

/** Un gabarit, tel qu'un écran le compose et qu'une base le range. */
export interface EmailTemplate {
  /** L'objet du message. ⚠️ Il porte des jetons comme le corps — « Vos {points} points » est légitime. */
  subject: string;
  /** Le titre affiché EN TÊTE du message, ou `null` quand le corps se suffit. */
  title: string | null;
  /** Le corps, en HTML restreint. */
  body: string;
  /** Le bouton d'action, ou `null`. */
  button: { label: string; url: string } | null;
}

/** Ce qu'un rendu produit — les trois parties qu'un transport attend. */
export interface RenderedTemplate {
  subject: string;
  html: string;
  text: string;
}

/**
 * VALIDE UN GABARIT CONTRE LES CHAMPS DÉCLARÉS, et rend sa forme assainie.
 *
 * ⚠️ 🔴 LE REFUS A LIEU À L'ÉCRITURE, quand un humain peut corriger — jamais au rendu, devant un
 * client. Un jeton inconnu qu'on laisserait passer partirait tel quel des milliers de fois.
 *
 * ⚠️ L'OBJET ET LE BOUTON SONT RELUS AUSSI : un jeton inconnu dans l'objet est exactement aussi
 * visible qu'un jeton inconnu dans le corps, et une URL de bouton est un `href` comme un autre.
 */
export function validateTemplate(tpl: EmailTemplate, fields: readonly MergeField[]): EmailTemplate {
  const premier = templateProblems(tpl, fields)[0];
  if (premier) throw new TemplateRejected(premier.code, problemMessage(premier));
  return {
    subject: tpl.subject,
    title: tpl.title,
    body: sanitizeHtml(tpl.body),
    // ⚠️ `boutonSur` ne fait plus que ROGNER le texte : un bouton incomplet ou dangereux a déjà été
    //    refusé ci-dessus. Le retirer en silence faisait disparaître à l'enregistrement un appel à
    //    l'action que l'opérateur venait d'écrire, sans un mot (§23.0).
    button: boutonSur(tpl.button, true),
  };
}

/** Ce qui empêche un gabarit d'être enregistré — un motif, et les jetons en cause quand il y en a. */
export type TemplateProblem =
  | { code: 'unknown_field'; tokens: string[] }
  | { code: 'missing_field'; tokens: string[] }
  | { code: 'empty_body' }
  | { code: 'invalid_button'; reason: 'incomplete' | 'unsafe' };

/**
 * TOUS LES PROBLÈMES D'UN GABARIT, dans l'ordre où un humain les corrige — vide = il s'enregistre.
 *
 * ⚠️ 🔴 C'EST LA RÈGLE, ET LA SEULE. `validateTemplate` lève sur le premier ; l'écran qui compose les
 * affiche tous, pendant la frappe. Les recopier côté écran ferait annoncer « prêt » un gabarit que le
 * serveur refuse — ou l'inverse —, et c'est exactement le défaut que l'écran dessiné voulait éviter.
 *
 * ⚠️ ON JUGE LE CORPS ASSAINI, pas le brut : une balise supprimée peut emporter du texte, donc un
 * jeton. Valider avant l'assainissement déclarerait valide un gabarit dont le jeton a disparu.
 */
export function templateProblems(tpl: EmailTemplate, fields: readonly MergeField[]): TemplateProblem[] {
  assertFields(fields);
  const connus = new Set(fields.map((f) => f.name));
  const corps = sanitizeHtml(tpl.body);
  const problems: TemplateProblem[] = [];

  const inconnus = [tpl.subject, tpl.title ?? '', corps, tpl.button?.label ?? '']
    .flatMap((s) => tokensOf(s))
    .filter((nom, i, tous) => !connus.has(nom) && tous.indexOf(nom) === i);
  if (inconnus.length > 0) problems.push({ code: 'unknown_field', tokens: inconnus });

  // ⚠️ 🔴 LES CHAMPS OBLIGATOIRES SONT EXIGÉS SUR L'ENSEMBLE du message, pas corps par corps : un
  //    lien peut légitimement vivre dans le BOUTON plutôt que dans le texte. Ce qui compte est que
  //    le destinataire l'ait quelque part.
  const cites = new Set([tpl.subject, tpl.title ?? '', corps, tpl.button?.label ?? '', tpl.button?.url ?? '']
    .flatMap((s) => tokensOf(s)));
  const manquants = fields.filter((f) => f.required && !cites.has(f.name)).map((f) => f.name);
  if (manquants.length > 0) problems.push({ code: 'missing_field', tokens: manquants });

  if (corps.replace(/<[^>]*>/g, '').trim() === '' && !tpl.title) problems.push({ code: 'empty_body' });

  // ⚠️ 🔴 LES DEUX MOITIÉS, OU AUCUNE — et la règle vit ICI, en UN seul endroit. Un bouton dont
  //    l'adresse n'est pas sûre, ou dont le texte est vide, n'est pas un bouton : le premier
  //    afficherait un appel à l'action qui ne mène nulle part, le second ne s'afficherait pas du tout.
  // ⚠️ À L'ÉCRITURE, l'adresse peut n'être qu'un jeton (`{lien}`) : seule la fusion connaîtra la vraie.
  if (tpl.button) {
    const label = tpl.button.label.trim();
    const url = tpl.button.url.trim();
    if (label === '' || url === '') problems.push({ code: 'invalid_button', reason: 'incomplete' });
    else if (!urlSure(url) && !estJetonSeul(url)) problems.push({ code: 'invalid_button', reason: 'unsafe' });
  }
  return problems;
}

/**
 * Le motif d'un problème, en une phrase — celle que porte le refus du serveur.
 * ⚠️ L'écran qui compose écrit SES phrases (traduites) à partir du code : celle-ci est le message d'une
 * erreur d'API, que l'écran affiche tel quel quand un appelant passe outre la vérification.
 */
function problemMessage(p: TemplateProblem): string {
  switch (p.code) {
    case 'unknown_field':
      return `Champ de fusion inconnu : ${p.tokens.map((n) => `« {${n}} »`).join(', ')}.`;
    case 'missing_field':
      return `Le champ ${p.tokens.map((n) => `« {${n}} »`).join(', ')} est obligatoire ici : sans lui, `
        + 'le destinataire ne peut rien faire du message.';
    case 'empty_body':
      return 'Un gabarit sans corps ni titre n’envoie rien.';
    case 'invalid_button':
      return p.reason === 'incomplete'
        ? 'Un bouton, c’est ses deux moitiés : un texte ET une adresse — ou aucun des deux.'
        : 'L’adresse du bouton n’est pas une adresse sûre (https://, http://, mailto:, tel:) ni un jeton seul.';
  }
}

/**
 * UNE URL ENTIÈREMENT FAITE D’UN JETON — `{lien}`.
 *
 * ⚠️ 🔴 ELLE N’EST ACCEPTABLE QU’À L’ÉCRITURE, où la vraie adresse n’existe pas encore. Une invitation
 * dont le bouton mène au dossier du destinataire ne peut pas nommer cette adresse au moment où on
 * compose le message — c’est la fusion qui la connaît.
 */
function estJetonSeul(url: string): boolean {
  // ⚠️ 🔴 LE MOTIF N’EST PAS RÉÉCRIT : `tokensOf` porte DÉJÀ ce qu’est un jeton. Un second motif
  //    « à peu près pareil » divergerait dès la première correction de l’un des deux (Forme A).
  const nu = url.trim();
  const jetons = tokensOf(nu);
  return jetons.length === 1 && nu === `{${jetons[0]}}`;
}

/**
 * UN BOUTON QUI EN EST UN — les deux moitiés, ou rien.
 *
 * ⚠️ 🔴 UNE SEULE DÉFINITION, et c'est le point. La même règle écrite chez l'appelant y serait
 * inerte : le cœur la rend déjà, donc aucun cas ne pourrait la faire tomber — un contrôle qu'on ne
 * peut pas voir échouer ne protège de rien, il rassure. ⚠️ La LECTURE d'une ligne ancienne garde sa
 * propre tolérance : c'est un autre travail, sur des lignes que cette fonction n'a jamais vues.
 * ⚠️ `differe` DIT QU’ON EST À L’ÉCRITURE : l’adresse peut alors n’être qu’un jeton, que seule
 * la fusion saura résoudre. Au RENDU il vaut faux, et le schéma est jugé sur l’adresse RÉELLE.
 */
function boutonSur(b: EmailTemplate['button'], differe = false): EmailTemplate['button'] {
  if (!b) return null;
  const label = b.label.trim();
  const adresse = differe ? urlSure(b.url) || estJetonSeul(b.url) : urlSure(b.url);
  return label !== '' && adresse ? { label, url: b.url } : null;
}

/**
 * COMPOSE UN CORPS À PARTIR DE SES MORCEAUX — un titre, le corps, un appel à l'action.
 *
 * 🔴 POURQUOI CETTE PORTE EXISTE. L'assistant de campagne fait saisir un **titre** et un **bouton**,
 * les dessine dans son aperçu… et ne les envoie nulle part : la campagne n'a que `subject` et `body`,
 * et le formulaire les jetait au moment de créer. Le commerçant composait un appel à l'action que son
 * client ne recevait jamais (§23.0, une option offerte sans effet).
 *
 * ⚠️ 🔴 LA COMPOSITION A LIEU À L'ÉCRITURE, PAS À L'ENVOI. Ce qui est stocké EST ce qui part — donc
 * l'aperçu peut rendre exactement le même texte, et les trois cessent de pouvoir diverger. Composer à
 * l'envoi aurait exigé des colonnes pour le titre et le bouton, et fait exister un troisième état du
 * message que personne ne relit.
 *
 * ⚠️ 🔴 LE TITRE ET LE LIBELLÉ SONT ÉCHAPPÉS : ils viennent d'un champ de formulaire, pas d'un éditeur
 * relu par nous. ⚠️ Les JETONS y survivent (`{prénom}`), parce que l'échappement ne touche pas les
 * accolades — un titre « Bonjour {prénom} » reste fusionnable.
 *
 * ⚠️ 🔴 LE BOUTON EST UN LIEN, PAS UNE PASTILLE, et ça se voit. L'assainisseur n'admet pas d'attribut
 * `style` : un appel à l'action DESSINÉ appartient à une enveloppe, donc à `renderTemplate`. Rendre
 * ici une pastille exigerait d'ouvrir la liste d'admission — une décision de sécurité, pas un détail
 * de mise en page. L'aperçu doit donc montrer un lien, sinon il reprend son mensonge par un autre bout.
 */
export function composeBody(p: {
  body: string;
  title?: string | null;
  button?: { label: string; url: string } | null;
}): string {
  const titre = (p.title ?? '').trim();
  // ⚠️ LA RÈGLE DU BOUTON N'EST PAS RÉÉCRITE : `boutonSur` la porte déjà, et l'écrire deux fois
  //    laisserait passer ici ce que le rendu refuse ailleurs.
  const bouton = boutonSur(p.button ?? null);
  return sanitizeHtml([
    titre === '' ? '' : `<h2>${escapeHtml(titre)}</h2>`,
    p.body,
    bouton === null ? '' : `<p><a href="${escapeHtml(bouton.url)}">${escapeHtml(bouton.label)}</a></p>`,
  ].filter((m) => m !== '').join(''));
}

/** Les valeurs d'un destinataire — ce que le consommateur sait de lui. */
export type MergeValues = Readonly<Record<string, string | number | null | undefined>>;

/**
 * Remplace les jetons d'un texte.
 * ⚠️ `echappe` DIT SI LA SORTIE EST DU HTML : dans le corps, une valeur doit être échappée ; dans la
 * partie TEXTE et dans l'objet, elle ne doit surtout pas l'être — un client lirait « Jean &amp; Fils ».
 */
function fusionner(
  source: string, fields: readonly MergeField[], values: MergeValues, echappe: boolean,
): string {
  const repli = new Map(fields.map((f) => [f.name, f.fallback]));
  return source.replace(JETON_ET_SON_ESPACE, (entier: string, espace: string, nom: string, pos: number, tout: string) => {
    // ⚠️ UN JETON NON DÉCLARÉ RESTE TEL QUEL, et c'est volontaire : `validateTemplate` l'a déjà refusé
    //    à l'écriture, donc le rencontrer ici signale une donnée écrite AVANT la règle. Le remplacer
    //    par du vide effacerait la trace de ce qu'il faut corriger.
    if (!repli.has(nom)) return entier;
    const v = values[nom];
    const texte = v === null || v === undefined || v === '' ? repli.get(nom)! : String(v);
    if (texte !== '') return espace + (echappe ? escapeHtml(texte) : texte);
    // ⚠️ 🔴 UN JETON QUI DISPARAÎT EMPORTE L'ESPACE QUI LE PRÉCÈDE, quand ce qui suit est une
    //    ponctuation, un espace, une balise ou la fin : « Bonjour {prénom}, » sans prénom doit se lire
    //    « Bonjour, » — pas « Bonjour , », que le client voit comme une coquille. Devant une LETTRE,
    //    l'espace reste : il sépare deux mots.
    return finDeMot(tout, pos + entier.length) ? '' : espace;
  });
}

/** Un jeton, précédé des espaces qu'il emporte s'il disparaît (`&nbsp;` compris : l'éditeur en pose). */
const JETON_ET_SON_ESPACE = new RegExp(`((?:[ \\t]|&nbsp;)*)${TOKEN_PATTERN.source}`, 'gu');

/** Ce qui suit la position termine-t-il un mot — fin, espace, ponctuation, balise ? */
function finDeMot(texte: string, pos: number): boolean {
  if (pos >= texte.length || texte.startsWith('&nbsp;', pos)) return true;
  return /[\s,.;:!?)<]/u.test(texte.charAt(pos));
}

/**
 * FUSIONNE UN CORPS HTML DONT L'ENVELOPPE N'EST PAS LA NÔTRE — assaini, valeurs échappées.
 *
 * ⚠️ 🔴 POURQUOI CETTE PORTE EXISTE À CÔTÉ DE `renderTemplate`. Une campagne marketing est un corps
 * que le COMMERÇANT possède : l'assistant lui en montre un aperçu, avec SON en-tête et SON pied.
 * L'habiller de notre carte au moment d'envoyer ferait recevoir au client autre chose que ce que
 * l'écran promettait (§23.0) — un test existant l'a d'ailleurs refusé, et il avait raison.
 *
 * ⚠️ `renderTemplate` reste la porte des messages dont l'enveloppe EST la nôtre (facture, courrier de
 * la Console). Les deux partagent l'assainissement, la fusion et l'échappement ; elles diffèrent sur
 * la seule chose qui les sépare vraiment : à qui appartient la mise en page.
 */
export function mergeHtml(
  source: string, fields: readonly MergeField[], values: MergeValues,
): string {
  // ⚠️ L’ORDRE COMPTE : on assainit le GABARIT, puis on fusionne en ÉCHAPPANT les valeurs — l’inverse
  //    ferait relire un nom de client par l’assainisseur, qui pourrait l’interpréter comme du balisage.
  return fusionner(sanitizeHtml(source), fields, values, true);
}

/**
 * FUSIONNE UN TEXTE QUI N’EST PAS DU HTML — un SMS, le titre d’un push, un objet.
 *
 * ⚠️ 🔴 AUCUN ÉCHAPPEMENT, ET C’EST TOUT L’INTÉRÊT. Un SMS n’est pas du balisage : y échapper une
 * valeur ferait lire « Jean &amp; Fils » au destinataire. C’est la SORTIE qui décide de
 * l’échappement, jamais la valeur — et un canal qui se trompe de fonction le montre au premier nom
 * à apostrophe.
 *
 * ⚠️ Les mêmes règles s’appliquent par ailleurs : une valeur manquante tombe sur le REPLI déclaré,
 * jamais sur le jeton.
 */
export function mergeText(
  source: string, fields: readonly MergeField[], values: MergeValues,
): string {
  return fusionner(source, fields, values, false);
}

/**
 * LE TEXTE BRUT D'UN CORPS HTML — dérivé, jamais saisi.
 * ⚠️ LES SAUTS DE LIGNE VIENNENT DES BALISES DE BLOC : un corps rendu sans eux serait un seul
 * paragraphe illisible, et c'est la partie que lisent les filtres anti-spam.
 * ⚠️ LES ENTITÉS SONT DÉCODÉES EN DERNIER : un `&lt;` doit ressortir en `<` dans du texte brut.
 */
export function htmlToText(html: string): string {
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h1|h2|h3|li|ul|ol)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Comment le message est habillé — le nom de l'application et sa couleur d'accent. */
export interface Branding {
  appName?: string;
  accent?: string;
}

const ACCENT = '#12b886';

/**
 * REND UN GABARIT POUR UN DESTINATAIRE.
 *
 * ⚠️ 🔴 LE GABARIT EST RÉ-ASSAINI AU RENDU. Il l'a déjà été à l'écriture — mais une ligne écrite
 * AVANT que la règle existe, ou restaurée depuis une sauvegarde ancienne, n'est jamais passée par là.
 * Le second passage est gratuit et c'est le seul qui couvre le passé.
 *
 * ⚠️ 🔴 ET L'ORDRE COMPTE : on assainit le GABARIT, puis on fusionne en ÉCHAPPANT les valeurs.
 * L'inverse — fusionner puis assainir — ferait relire une valeur de client par l'assainisseur, qui
 * pourrait alors interpréter comme du balisage ce qui n'est qu'un nom.
 */
export function renderTemplate(
  tpl: EmailTemplate, fields: readonly MergeField[], values: MergeValues, b: Branding = {},
): RenderedTemplate {
  const accent = b.accent ?? ACCENT;
  const app = b.appName ?? 'Orgazone';

  const corps = fusionner(sanitizeHtml(tpl.body), fields, values, true);
  const titre = tpl.title ? fusionner(escapeHtml(tpl.title), fields, values, true) : null;
  const subject = fusionner(tpl.subject, fields, values, false);

  // ⚠️ 🔴 L’ADRESSE DU BOUTON EST FUSIONNÉE, PUIS SON SCHÉMA EST JUGÉ — dans CET ordre, et c’est
  //    l’INVERSE de la règle du corps. Dans le corps, le gabarit est à nous et la valeur est une
  //    donnée : on assainit d’abord. Ici la valeur DEVIENT l’adresse, donc elle doit être relue :
  //    juger `{lien}` avant la fusion laisserait passer n’importe quel `javascript:` venu de la donnée.
  const fusionne = tpl.button
    ? { label: tpl.button.label, url: fusionner(tpl.button.url, fields, values, false) }
    : null;
  const sur = boutonSur(fusionne);
  const bouton = sur
    ? { label: fusionner(escapeHtml(sur.label), fields, values, true), url: sur.url }
    : null;

  const html = `<!doctype html><html><body style="margin:0;background:#f4f6f7;font-family:Arial,Helvetica,sans-serif;color:#22282c">
  <div style="max-width:520px;margin:0 auto;padding:24px">
    <div style="font-size:13px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${accent};margin-bottom:14px">${escapeHtml(app)}</div>
    <div style="background:#fff;border:1px solid #e2e6ea;border-radius:16px;padding:26px">
      ${titre ? `<h1 style="margin:0 0 14px;font-size:20px;color:#0c0e10">${titre}</h1>` : ''}
      <div style="font-size:14px;line-height:1.6;overflow-wrap:anywhere">${corps}</div>
      ${bouton ? `<div style="margin-top:20px"><a href="${escapeHtml(bouton.url)}" rel="noopener noreferrer" target="_blank" style="display:inline-block;background:${accent};color:#fff;text-decoration:none;font-weight:600;padding:12px 22px;border-radius:11px">${bouton.label}</a></div>` : ''}
    </div>
  </div></body></html>`;

  // ⚠️ LE TEXTE SE DÉRIVE DU CORPS FUSIONNÉ, et le bouton y devient « libellé : url » — un lecteur en
  //    texte seul doit pouvoir atteindre la page, sinon l'appel à l'action ne le concerne pas.
  const text = [
    titre ? htmlToText(titre) : '',
    htmlToText(corps),
    bouton ? `${htmlToText(bouton.label)} : ${bouton.url}` : '',
  ].filter((p) => p !== '').join('\n\n');

  return { subject, html, text };
}
